import { redactText } from "@fabric-harness/sdk";
import { type DatabricksPrincipal, databricksIdentity } from "./identity.js";
import { databricksWorkspaceOrigin } from "./workspace-origin.js";

export type DatabricksWorkspaceApiMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export interface DatabricksWorkspaceApiOptions {
  host: string;
  principal: DatabricksPrincipal;
  fetchImpl?: typeof fetch;
  maxRetries?: number;
  retryBaseDelayMs?: number;
  signal?: AbortSignal;
  extraHeaders?:
    | Record<string, string>
    | (() => Record<string, string> | Promise<Record<string, string>>);
}

export interface DatabricksWorkspaceApiRequestOptions {
  /** JSON value by default, or a native fetch body when `bodyEncoding` is `raw`. */
  body?: unknown;
  bodyEncoding?: "json" | "raw";
  query?: Record<string, string | number | boolean | readonly string[] | undefined>;
  signal?: AbortSignal;
  /**
   * Reads retry transient failures automatically. Mutations retry only when explicitly marked
   * idempotent and accompanied by a stable idempotency key.
   */
  retry?: "safe" | "idempotent" | "never";
  idempotencyKey?: string;
  maxRetries?: number;
  headers?: Record<string, string>;
}

/**
 * Credential-safe native Databricks REST escape hatch.
 *
 * Prefer the generated clients from {@link databricksSdk} when one exists. This client preserves
 * access to newly released or uncommon Databricks APIs without waiting for a Harness wrapper. It is
 * deliberately not a model tool: applications must validate requests and place governed mutations
 * behind their own explicit tool/action boundary.
 */
export interface DatabricksWorkspaceApi {
  request<T>(
    method: DatabricksWorkspaceApiMethod,
    path: string,
    options?: DatabricksWorkspaceApiRequestOptions,
  ): Promise<T>;
  requestRaw(
    method: DatabricksWorkspaceApiMethod,
    path: string,
    options?: DatabricksWorkspaceApiRequestOptions,
  ): Promise<Response>;
}

export function databricksWorkspaceApi(
  options: DatabricksWorkspaceApiOptions,
): DatabricksWorkspaceApi {
  const host = databricksWorkspaceOrigin(options.host);
  const tokenProvider = databricksIdentity(options.principal);
  const fetchImpl = options.fetchImpl ?? fetch;
  const clientMaxRetries = boundedRetries(options.maxRetries ?? 3);
  const retryBaseDelayMs = options.retryBaseDelayMs ?? 500;
  if (!Number.isFinite(retryBaseDelayMs) || retryBaseDelayMs < 0)
    throw new Error("databricks workspace API: retryBaseDelayMs must be non-negative.");

  async function requestRaw(
    method: DatabricksWorkspaceApiMethod,
    path: string,
    requestOptions: DatabricksWorkspaceApiRequestOptions = {},
  ): Promise<Response> {
    const normalizedPath = nativePath(path);
    const headers = normalizedHeaders(requestOptions.headers);
    const retry = requestOptions.retry ?? (method === "GET" ? "safe" : "never");
    if (method !== "GET" && retry === "safe") {
      throw new Error(
        'databricks workspace API: mutations may retry only with `retry: "idempotent"`.',
      );
    }
    if (retry === "idempotent" && !requestOptions.idempotencyKey?.trim()) {
      throw new Error(
        "databricks workspace API: idempotent mutation retries require a stable idempotencyKey.",
      );
    }
    if (requestOptions.idempotencyKey) {
      headers.set("x-databricks-idempotency-token", requestOptions.idempotencyKey);
    }
    const maxRetries =
      retry === "never" ? 0 : boundedRetries(requestOptions.maxRetries ?? clientMaxRetries);
    const signal = combineAbortSignals(options.signal, requestOptions.signal);
    const url = new URL(`${host}${normalizedPath}`);
    for (const [key, value] of Object.entries(requestOptions.query ?? {})) {
      if (Array.isArray(value)) {
        for (const item of value) url.searchParams.append(key, item);
      } else if (value !== undefined) {
        url.searchParams.set(key, String(value));
      }
    }

    for (let attempt = 0; ; attempt += 1) {
      const extraHeaders =
        typeof options.extraHeaders === "function"
          ? await options.extraHeaders()
          : options.extraHeaders;
      const resolvedHeaders = normalizedHeaders(extraHeaders);
      for (const [key, value] of headers) resolvedHeaders.set(key, value);
      const token = await tokenProvider();
      resolvedHeaders.set("authorization", `Bearer ${token}`);
      if (
        requestOptions.body !== undefined &&
        requestOptions.bodyEncoding !== "raw" &&
        !resolvedHeaders.has("content-type")
      ) {
        resolvedHeaders.set("content-type", "application/json");
      }
      const init: RequestInit = {
        method,
        headers: resolvedHeaders,
        ...(requestOptions.body !== undefined
          ? {
              body:
                requestOptions.bodyEncoding === "raw"
                  ? (requestOptions.body as NonNullable<RequestInit["body"]>)
                  : JSON.stringify(requestOptions.body),
            }
          : {}),
        ...(signal ? { signal } : {}),
      };

      let response: Response;
      try {
        response = await fetchImpl(url, init);
      } catch (error) {
        if (signal?.aborted || attempt >= maxRetries) throw error;
        await abortableSleep(retryBaseDelayMs * 2 ** attempt, signal);
        continue;
      }
      if (response.ok) return response;
      if (isRetryableStatus(response.status) && attempt < maxRetries) {
        await response.body?.cancel();
        await abortableSleep(retryDelayMs(response, attempt, retryBaseDelayMs), signal);
        continue;
      }
      const detail = redactText(await response.text(), { additionalSecrets: [token] });
      throw new Error(
        `databricks workspace API: ${response.status} ${response.statusText}: ${detail.slice(0, 2000)}`,
      );
    }
  }

  return {
    async request<T>(
      method: DatabricksWorkspaceApiMethod,
      path: string,
      requestOptions: DatabricksWorkspaceApiRequestOptions = {},
    ): Promise<T> {
      const response = await requestRaw(method, path, requestOptions);
      if (response.status === 204) return undefined as T;
      return (await response.json()) as T;
    },
    requestRaw,
  };
}

function nativePath(path: string): string {
  if (
    !path.startsWith("/") ||
    path.includes("?") ||
    path.includes("#") ||
    path.includes("\\") ||
    path.startsWith("//")
  ) {
    throw new Error(
      "databricks workspace API: path must be an absolute workspace-relative path without query or fragment text.",
    );
  }
  if (!(path.startsWith("/api/") || path.startsWith("/serving-endpoints/"))) {
    throw new Error("databricks workspace API: path must target /api/* or /serving-endpoints/*.");
  }
  if (/%2f|%5c/iu.test(path)) {
    throw new Error("databricks workspace API: encoded path separators are not allowed.");
  }
  let segments: string[];
  try {
    segments = path.split("/").map((segment) => decodeURIComponent(segment));
  } catch {
    throw new Error("databricks workspace API: path contains invalid percent encoding.");
  }
  if (segments.some((segment) => segment === "." || segment === "..")) {
    throw new Error("databricks workspace API: relative path segments are not allowed.");
  }
  return path;
}

function normalizedHeaders(input: Record<string, string> | undefined): Headers {
  const headers = new Headers(input);
  if (headers.has("authorization") || headers.has("proxy-authorization")) {
    throw new Error(
      "databricks workspace API: authorization headers are managed by the configured principal.",
    );
  }
  return headers;
}

function boundedRetries(value: number): number {
  if (!Number.isInteger(value) || value < 0 || value > 10)
    throw new Error("databricks workspace API: maxRetries must be an integer from 0 to 10.");
  return value;
}

function isRetryableStatus(status: number): boolean {
  return status === 429 || status === 499 || (status >= 500 && status <= 599);
}

function retryDelayMs(response: Response, attempt: number, base: number): number {
  const retryAfter = response.headers.get("retry-after");
  if (retryAfter) {
    const seconds = Number(retryAfter);
    if (Number.isFinite(seconds) && seconds >= 0) return seconds * 1000;
  }
  return base * 2 ** attempt;
}

function combineAbortSignals(
  clientSignal?: AbortSignal,
  requestSignal?: AbortSignal,
): AbortSignal | undefined {
  if (!clientSignal) return requestSignal;
  if (!requestSignal || requestSignal === clientSignal) return clientSignal;
  return AbortSignal.any([clientSignal, requestSignal]);
}

function abortableSleep(ms: number, signal?: AbortSignal): Promise<void> {
  if (signal?.aborted)
    return Promise.reject(
      signal.reason ?? new DOMException("The operation was aborted.", "AbortError"),
    );
  if (!signal) return new Promise((resolve) => setTimeout(resolve, ms));
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      signal.removeEventListener("abort", abort);
      resolve();
    }, ms);
    const abort = () => {
      clearTimeout(timer);
      signal.removeEventListener("abort", abort);
      reject(signal.reason ?? new DOMException("The operation was aborted.", "AbortError"));
    };
    signal.addEventListener("abort", abort, { once: true });
  });
}
